// [src/pages/Ipt/Ipt.jsx]

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, TEST_API_BASE_URL } from "config/apiConfig";
import { getTokenStatus } from "utils/tokenManager";
import { useBranch } from 'contexts/BranchContext';

// CSS imports
import 'styles/Font.css';

// Components imports
import Footer from "components/layout/Footer/Footer";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";
import usePreventInputScroll from "hooks/usePreventInputScroll";
import { useForegroundRefetch } from 'hooks/useForegroundRefetch';

// Image imports
import dotIcon from 'assets/images/icon/dot-icon.svg';
import starIcon from 'assets/images/icon/star-icon.svg';
import crownIcon from 'assets/images/icon/crown-icon.svg';
import clockIcon from 'assets/images/icon/clock-icon.svg';
import calendarIcon from 'assets/images/icon/calendar-icon.svg';
import arrowRightIcon from 'assets/images/icon/arrow-right-icon.svg';
import defaultCoachImg from 'assets/images/coachSample/coach_ex1.svg';

import BottomSheet from 'components/ui/BottomSheet/BottomSheet';
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";

// --- 날짜 유틸리티 함수 ---
const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const cleaned = dateStr.replace(/[^0-9]/g, '');
    if (cleaned.length === 8) {
        return `${cleaned.substring(0, 4)}.${cleaned.substring(4, 6)}.${cleaned.substring(6, 8)}`;
    }
    return dateStr;
};

// 이용 기간 확인 (오늘 날짜가 종료일보다 같거나 이전이면 true)
const checkIsActive = (edate) => {
    if (!edate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const cleaned = edate.replace(/[^0-9]/g, '');
    if (cleaned.length !== 8) return false;

    const year = parseInt(cleaned.substring(0, 4));
    const month = parseInt(cleaned.substring(4, 6)) - 1;
    const day = parseInt(cleaned.substring(6, 8));
    const endDate = new Date(year, month, day);
    endDate.setHours(0, 0, 0, 0);

    return endDate >= today;
};

// 잔여 기간 계산 함수
const calculateRemainDays = (edate) => {
    if (!edate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const cleaned = edate.replace(/[^0-9]/g, '');
    if (cleaned.length !== 8) return null;

    const year = parseInt(cleaned.substring(0, 4));
    const month = parseInt(cleaned.substring(4, 6)) - 1;
    const day = parseInt(cleaned.substring(6, 8));
    const endDate = new Date(year, month, day);
    endDate.setHours(0, 0, 0, 0);

    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays >= 0 ? diffDays : null;
};

// --- 나의 구독 카드 컴포넌트 ---
const MySubscriptionCard = ({ item }) => {
    const hasCount = item.totalCnt !== null && item.totalCnt !== undefined && item.totalCnt > 0;
    const hasPeriod = item.period && item.period !== '';

    return (
        <div style={staticStyles.mySubProdBox}>
            <div style={staticStyles.mySubProdTitle}>
                <div style={staticStyles.mySubProdName}>
                    {item.productName}
                    <img alt="왕관" src={crownIcon} style={staticStyles.crownIcon} />
                    <div style={staticStyles.mySubState}>{item.status}</div>
                </div>

                <div style={staticStyles.cardDivider} />

                {hasPeriod && (
                    <div style={staticStyles.subPeriod}>
                        이용 기간
                        <span style={staticStyles.subPeriodValue}>{item.period}</span>
                    </div>
                )}

                {item.remainDays !== null && item.remainDays !== undefined && (
                    <div style={{ ...staticStyles.subPeriod, marginTop: '8px' }}>
                        잔여 기간
                        <span style={staticStyles.subPeriodValue}>잔여 {item.remainDays}일</span>
                    </div>
                )}

                {hasCount && (
                    <>
                        <div style={{ ...staticStyles.subPeriod, marginTop: '8px' }}>
                            이용 횟수
                            <span style={staticStyles.subPeriodValue}>
                                {item.useCnt} / {item.totalCnt}회
                            </span>
                        </div>
                        <div style={{ ...staticStyles.subPeriod, marginTop: '8px' }}>
                            잔여 횟수
                            <span style={staticStyles.subPeriodValue}>
                                잔여 {item.totalCnt - item.useCnt}회
                            </span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// --- 수업 카드 컴포넌트 ---
const ClassCard = ({ exercise, date, onClick, onTimeClick }) => {
    return (
        <div style={scheduleStyles.classCard} onClick={() => onClick && onClick(exercise)}>
            <div style={scheduleStyles.classInfoMain}>
                <img
                    src={exercise.img || defaultCoachImg}
                    alt="수업"
                    style={scheduleStyles.classImg}
                />
                <div style={scheduleStyles.classTextContent}>
                    <p style={scheduleStyles.className}>{exercise.exerciseName}</p>
                    <p style={scheduleStyles.classIntro}>{exercise.intro}</p>
                </div>
            </div>

            <div style={scheduleStyles.timeSlotList}>
                {exercise.times && exercise.times.map((t, idx) => (
                    <div
                        key={t.stKey ? `slot-${t.stKey}-${idx}` : `idx-${idx}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (t.isAvailable === 'Y') {
                                onTimeClick && onTimeClick(exercise.ctKey, date, t.time);
                            }
                        }}
                        style={{
                            ...scheduleStyles.timeSlot,
                            ...(t.isAvailable === 'N' ? scheduleStyles.timeSlotDisabled : {})
                        }}
                    >
                        <span style={scheduleStyles.timeSlotText}>
                            {t.time} {t.isAvailable === 'Y' ? `(${t.totalCnt - t.remCnt}/${t.totalCnt})` : '(마감)'}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- 날짜 섹션 컴포넌트 ---
const DaySection = ({ day, onClassClick, onTimeClick }) => {
    const formatDateLabel = (dateStr) => {
        if (!dateStr) return "";
        const parts = dateStr.split('-');
        if (parts.length < 3) return dateStr;
        return `${parseInt(parts[1])}. ${parseInt(parts[2])}`;
    };

    return (
        <div style={scheduleStyles.dayContainer}>
            <div style={scheduleStyles.dayHeader}>
                <span style={scheduleStyles.dayLabel}>{formatDateLabel(day.date)} ({day.dayName})</span>
            </div>
            <div style={scheduleStyles.classList}>
                {day.exercises && day.exercises.map((ex, idx) => (
                    <ClassCard
                        key={ex.ctKey ? `class-${ex.ctKey}-${idx}` : `idx-${idx}`}
                        exercise={ex}
                        date={day.date}
                        onClick={onClassClick}
                        onTimeClick={onTimeClick}
                    />
                ))}
            </div>
        </div>
    );
};

// --- 나의 예약 카드 컴포넌트 ---
const MyReserveCard = ({ item, onClick }) => {
    return (
        <div style={myReserveStyles.card} onClick={() => onClick && onClick(item)}>
            <img
                src={item.img || defaultCoachImg}
                alt="코치"
                style={myReserveStyles.coachImg}
            />
            <div style={myReserveStyles.infoContainer}>
                <span style={myReserveStyles.coachName}>{item.coachName}</span>
                <div style={myReserveStyles.dateTimeInfo}>
                    <div style={myReserveStyles.dateWrapper}>
                        <img src={calendarIcon} alt="날짜" style={myReserveStyles.icon} />
                        <span style={myReserveStyles.dateText}>{item.date}</span>
                    </div>
                    <img src={dotIcon} alt="dot" style={myReserveStyles.dotDivider} />
                    <div style={myReserveStyles.timeWrapper}>
                        <img src={clockIcon} alt="시간" style={myReserveStyles.icon} />
                        <span style={myReserveStyles.timeText}>{item.time}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// 나의 예약 목록 섹션
const MyReserveSection = ({ list, onMoreClick }) => {
    return (
        <div style={myReserveStyles.container}>
            <style>{`
                .hide-scrollbar::-webkit-scrollbar { display: none; }
                .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>

            <div style={myReserveStyles.titleWrapper} onClick={onMoreClick}>
                <span style={myReserveStyles.title}>예약 중인 수업</span>
                <img src={arrowRightIcon} alt="더보기" style={myReserveStyles.arrowIcon} />
            </div>

            <div className="hide-scrollbar" style={myReserveStyles.listContainer}>
                {list && list.length > 0 ? (
                    list.map((res) => (
                        <MyReserveCard
                            key={res.id}
                            item={res}
                        />
                    ))
                ) : (
                    <div style={{ ...myReserveStyles.card, justifyContent: 'center', color: '#747474' }}>
                        예약된 내역이 없습니다.
                    </div>
                )}
            </div>
        </div>
    );
};

// 이전 이용 내역 섹션
const PastReserveSection = ({ list }) => {
    return (
        <div style={myReserveStyles.container}>
            <div style={myReserveStyles.titleWrapper}>
                <span style={myReserveStyles.title}>이전 이용 내역</span>
            </div>

            <div className="hide-scrollbar" style={myReserveStyles.listContainer}>
                {list && list.length > 0 ? (
                    list.map((res) => (
                        <MyReserveCard
                            key={res.id}
                            item={res}
                        />
                    ))
                ) : (
                    <div style={{ ...myReserveStyles.card, justifyContent: 'center', color: '#747474' }}>
                        이전 이용 내역이 없습니다.
                    </div>
                )}
            </div>
        </div>
    );
};

function Ipt() {
    usePreventInputScroll();
    usePreventHorizontalScroll();
    const navigate = useNavigate();
    const { currentBranch } = useBranch();

    const brKey = currentBranch.brKey;
    const brName = currentBranch.brName;
    const stKind = 'G'; // IPT stKind
    const [mySubscriptionList, setMySubscriptionList] = useState([]);
    const [myIptList, setMyIptList] = useState([]);
    const [activeTab, setActiveTab] = useState('IPT');
    const [scheduleList, setScheduleList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [showSheet, setShowSheet] = useState(false);
    const [sheetTimes, setSheetTimes] = useState([]);
    const [currentSelectedDate, setCurrentSelectedDate] = useState("");
    const [selectedCtKey, setSelectedCtKey] = useState("");
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [pendingReservation, setPendingReservation] = useState(null);
    const [slickStatus, setSlickStatus] = useState('N');
    const [initialTimeData, setInitialTimeData] = useState(null);
    const [alertModal, setAlertModal] = useState({
        isOpen: false,
        title: '',
        message: ''
    });

    const handleMoreReservations = useCallback(() => {
        navigate('/sb/reserve');
    }, [navigate]);

    const fetchUserStatus = useCallback(async () => {
        const { token } = getTokenStatus();
        if (!token) return;

        try {
            const response = await fetch(`${API_BASE_URL}/home`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                },
                body: new URLSearchParams({ brKey: brKey })
            });

            if (response.ok) {
                const data = await response.json();
                const resData = data.data || data;
                if (resData.slickStatus) {
                    setSlickStatus(resData.slickStatus);
                }
            }
        } catch (e) {
            console.error("Failed to fetch user status:", e);
        }
    }, [brKey]);

    useEffect(() => {
        fetchUserStatus();
    }, [fetchUserStatus]);

    const handleDateChange = useCallback(async (date, targetCtKey) => {
        const ctKeyToUse = targetCtKey || selectedCtKey;
        setCurrentSelectedDate(date);
        const { token } = getTokenStatus();
        if (!token || !ctKeyToUse) return;

        try {
            const SCHEDULE_API_URL = `${API_BASE_URL}/schedule?brKey=${brKey}&ctKey=${ctKeyToUse}&stKind=${stKind}&date=${date}`;
            const response = await fetch(SCHEDULE_API_URL, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const resData = await response.json();
            const data = resData.data || resData;

            let times = [];
            if (data.list && Array.isArray(data.list)) {
                times = data.list;
            } else if (data.mnInfo && data.mnInfo.times) {
                times = data.mnInfo.times;
            } else if (data.times) {
                times = data.times;
            }

            setSheetTimes(times);
        } catch (e) {
            console.error("Schedule Fetch Error:", e);
            setSheetTimes([]);
        }
    }, [selectedCtKey, brKey, stKind]);

    const handleTimeSlotClick = useCallback(async (ctKey, date, timeStr) => {
        if (slickStatus !== 'Y') {
            setAlertModal({
                isOpen: true,
                title: '예약 불가',
                message: `[${currentBranch?.brName}] 수업 예약 권한이 없습니다.\n(이용권을 구매해주세요)`
            });
            return;
        }

        if (timeStr) {
            const parts = timeStr.split(':');
            let hour = parseInt(parts[0], 10);
            const minute = parts[1];
            const period = hour >= 12 ? '오후' : '오전';
            let displayHour = hour > 12 ? hour - 12 : hour;
            if (displayHour === 0) displayHour = 12;
            const formattedTime = `${period} ${displayHour}:${minute}`;
            setInitialTimeData({ time: formattedTime });
        } else {
            setInitialTimeData(null);
        }

        setSelectedCtKey(ctKey);
        await handleDateChange(date, ctKey);
        setShowSheet(true);
    }, [handleDateChange, slickStatus]);

    const handleSheetConfirm = useCallback((data) => {
        setPendingReservation(data);
        setIsConfirmModalOpen(true);
    }, []);

    const handleFinalReservation = useCallback(async () => {
        if (!pendingReservation) return;

        const { stKey } = pendingReservation;
        const { token } = getTokenStatus();

        const payload = {
            brKey: brKey,
            stKey: String(stKey),
            stKind: stKind
        };

        try {
            const response = await fetch(`${API_BASE_URL}/regSchedule`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                },
                body: new URLSearchParams(payload)
            });

            const resData = await response.json();
            const code = resData.data?.code || resData.code;
            const isSuccess = resData.status === 'success' || code === 200 || code === '200' || code === '0000';

            if (isSuccess) {
                setIsConfirmModalOpen(false);
                setShowSheet(false);
                alert("예약이 완료되었습니다.");
            } else {
                const errorMsg = resData.data?.message || "예약에 실패했습니다.";
                alert(errorMsg);
            }
        } catch (e) {
            console.error("Reservation Error:", e);
            alert("예약 중 오류가 발생했습니다.");
        }
    }, [pendingReservation, brKey, stKind]);

    const fetchClassList = useCallback(async () => {
        const { token } = getTokenStatus();
        setIsLoading(true);
        try {
            const queryParams = new URLSearchParams({
                brKey: brKey,
                stKind: stKind,
                weekOffset: 0
            }).toString();

            const CLASS_LIST_API_URL = `${API_BASE_URL}/weekSchedule?${queryParams}`;

            const response = await fetch(CLASS_LIST_API_URL, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`,
                }
            });

            const data = await response.json();
            const resData = data.data || data;

            if (resData.list && Array.isArray(resData.list)) {
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const date = String(now.getDate()).padStart(2, '0');
                const todayStr = `${year}-${month}-${date}`;

                const filteredList = resData.list.filter(day =>
                    day.date >= todayStr && day.exercises && day.exercises.length > 0
                );
                setScheduleList(filteredList);
            } else {
                setScheduleList([]);
            }
        } catch (e) {
            console.error("❌ 수업 목록 조회 에러:", e);
            setScheduleList([]);
        } finally {
            setIsLoading(false);
        }
    }, [brKey, stKind]);

    useEffect(() => {
        if (activeTab === 'IPT') {
            fetchClassList();
        }
    }, [activeTab, fetchClassList]);

    const fetchReserveList = useCallback(async () => {
        const { token } = getTokenStatus();
        setIsLoading(true);
        try {
            const RESERVE_API_URL = `${API_BASE_URL}/reserve`;

            const response = await fetch(RESERVE_API_URL, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error(`API Error: ${response.status}`);

            const data = await response.json();
            const resData = data.data || data;

            let rawList = resData.list;
            if (!rawList) {
                setMyIptList([]);
            } else {
                if (!Array.isArray(rawList)) rawList = [rawList];

                const iptOnlyList = rawList.filter(item => item.stKind === 'G');

                const formattedList = iptOnlyList.map((item) => ({
                    id: item.shKey,
                    date: item.date,
                    time: item.time,
                    coachName: item.mnName || '담당 코치',
                    img: item.img || defaultCoachImg,
                    status: item.status || '예약확정',
                    centerName: brName,
                    stKind: item.stKind
                }));

                setMyIptList(formattedList);
            }
        } catch (error) {
            console.error("Reserve List Fetch Error:", error);
            setMyIptList([]);
        } finally {
            setIsLoading(false);
        }
    }, [brName]);

    useEffect(() => {
        if (activeTab === 'MY_IPT') {
            fetchReserveList();
        }
    }, [activeTab, fetchReserveList]);

    // 포그라운드 진입 시 데이터 최신화
    const handleRefetch = useCallback(() => {
        fetchUserStatus();
        if (activeTab === 'IPT') {
            fetchClassList();
        } else if (activeTab === 'MY_IPT') {
            fetchReserveList();
        }
    }, [activeTab, fetchUserStatus, fetchClassList, fetchReserveList]);

    useForegroundRefetch(handleRefetch);

    useEffect(() => {
        if (activeTab === 'MY_IPT') {
            const fetchOneOrder = async () => {
                const { token } = getTokenStatus();
                try {
                    const response = await fetch(`${API_BASE_URL}/oneOrder`, {
                        // const response = await fetch(`${TEST_API_BASE_URL}/oneOrder`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                            'Authorization': `Bearer ${token}`
                        },
                        body: new URLSearchParams({ pdKind: 'B' })
                    });

                    if (response.ok) {
                        const data = await response.json();
                        const orderInfo = data.info || data.data?.info;

                        if (orderInfo && orderInfo.odKey) {
                            const sdate = formatDate(orderInfo.sdate);
                            const edate = formatDate(orderInfo.edate);
                            const isActive = checkIsActive(orderInfo.edate);
                            const remainDays = calculateRemainDays(orderInfo.edate);
                            const periodStr = (sdate && edate) ? `${sdate} ~ ${edate}` : '';
                            const statusStr = isActive ? '이용 중' : '기간 만료';

                            setMySubscriptionList([{
                                id: orderInfo.odKey,
                                productName: orderInfo.name || 'IPT 멤버십',
                                status: statusStr,
                                period: periodStr,
                                remainDays: remainDays,
                                useCnt: (orderInfo.useCnt !== undefined && orderInfo.useCnt !== null) ? Number(orderInfo.useCnt) : null,
                                totalCnt: (orderInfo.totalCnt !== undefined && orderInfo.totalCnt !== null) ? Number(orderInfo.totalCnt) : null,
                                price: orderInfo.price ? Number(orderInfo.price).toLocaleString() : ''
                            }]);
                        } else {
                            setMySubscriptionList([]);
                        }
                    } else {
                        setMySubscriptionList([]);
                    }
                } catch (e) {
                    console.error("OneOrder API Error:", e);
                    setMySubscriptionList([]);
                }
            };

            fetchOneOrder();
        }
    }, [activeTab]);

    const now = new Date();
    const parseDateTime = (dateStr, timeStr) => {
        try {
            let time = timeStr;
            const dt = new Date(`${dateStr} ${time}`);
            if (isNaN(dt.getTime())) return new Date(dateStr);
            return dt;
        } catch (e) {
            return new Date();
        }
    };

    const activeReserveList = myIptList.filter(item => {
        const itemDate = parseDateTime(item.date, item.time);
        const isFuture = itemDate >= now;
        const isActiveStatus = item.status !== '취소됨' && item.status !== '수강완료';
        return isFuture && isActiveStatus;
    }).sort((a, b) => {
        const dateA = parseDateTime(a.date, a.time);
        const dateB = parseDateTime(b.date, b.time);
        return dateA - dateB;
    });

    const pastReserveList = myIptList.filter(item => {
        const itemDate = parseDateTime(item.date, item.time);
        const isPast = itemDate < now;
        const isDoneStatus = item.status === '취소됨' || item.status === '수강완료';
        return isPast || isDoneStatus;
    }).sort((a, b) => {
        const dateA = parseDateTime(a.date, a.time);
        const dateB = parseDateTime(b.date, b.time);
        return dateB - dateA;
    });

    const getTabStyle = (tabName) => ({
        ...staticStyles.boostButtonBase, span: {
            ...staticStyles.boostButtonTextBase, ...(tabName === activeTab ? staticStyles.boostButtonTextActive : staticStyles.boostButtonTextInactive)
        }, indicator: {
            ...staticStyles.boostButtonIndicatorBase, ...(tabName === activeTab ? staticStyles.boostButtonIndicatorActive : staticStyles.boostButtonIndicatorInactive)
        }
    });

    return (
        <div className="backgroundWrapper" style={staticStyles.backgroundWrapper}>
            <main style={staticStyles.mainContent}>
                <div style={staticStyles.branchTitleContainer}>
                    <h2 style={staticStyles.branchTitle}>{brName}</h2>
                </div>

                <div className="boostSelectContainer" style={staticStyles.boostSelectContainer}>
                    <button className="boostMain" onClick={() => setActiveTab('IPT')}
                        style={staticStyles.boostButtonBase}>
                        <span style={getTabStyle('IPT').span}>IPT</span>
                        <div className="horizonButtonBar" style={getTabStyle('IPT').indicator} />
                    </button>
                    <button className="myBoost" onClick={() => setActiveTab('MY_IPT')}
                        style={staticStyles.boostButtonBase}>
                        <span style={getTabStyle('MY_IPT').span}>나의 IPT</span>
                        <div className="horizonButtonBar" style={getTabStyle('MY_IPT').indicator} />
                    </button>
                </div>

                <div style={{ width: '100%' }}>
                    {activeTab === 'IPT' ? (
                        <div className="BoostScheduleList" style={staticStyles.boostCoachCardList}>
                            {isLoading ? (
                                <div style={{ color: 'white', textAlign: 'center', padding: '20px' }}>로딩 중...</div>
                            ) : scheduleList.length > 0 ? (
                                scheduleList.map((day, index) => (
                                    <DaySection
                                        key={day.date ? `day-${day.date}-${index}` : `idx-${index}`}
                                        day={day}
                                        onClassClick={(ex) => {
                                            navigate('/ipt/detail', {
                                                state: {
                                                    ctKey: ex.ctKey,
                                                    stKind: stKind,
                                                    brKey: brKey,
                                                    exercise: ex,
                                                    date: day.date
                                                }
                                            });
                                        }}
                                        onTimeClick={handleTimeSlotClick}
                                    />
                                ))
                            ) : (
                                <div style={{ color: '#9A9A9A', textAlign: 'center', padding: '20px' }}>
                                    예약 가능한 수업 일정이 없습니다.
                                </div>
                            )}
                        </div>) : (
                        <>
                            <div className="MySubProd" style={staticStyles.mySubProdContainer}>
                                <div style={staticStyles.mySubProdHeader}>구독 중인 IPT</div>
                                {mySubscriptionList.length > 0 ? (
                                    mySubscriptionList.map((sub) => (
                                        <MySubscriptionCard key={sub.id} item={sub} />
                                    ))
                                ) : (
                                    <div style={{
                                        color: '#9A9A9A',
                                        textAlign: 'center',
                                        padding: '40px 0',
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        fontFamily: 'Pretendard',
                                        fontSize: '14px'
                                    }}>
                                        구독 중인 상품이 없습니다.
                                    </div>)}
                            </div>

                            <MyReserveSection
                                list={activeReserveList}
                                onMoreClick={handleMoreReservations}
                            />

                            <PastReserveSection
                                list={pastReserveList}
                            />
                        </>)}
                </div>
                <Footer />
            </main>

            {showSheet && (
                <BottomSheet
                    show={showSheet}
                    onClose={() => {
                        setShowSheet(false);
                        setInitialTimeData(null);
                    }}
                    availableTimes={sheetTimes}
                    onDateChange={(date) => handleDateChange(date)}
                    onConfirm={handleSheetConfirm}
                    selectedDate={currentSelectedDate}
                    initialData={initialTimeData}
                />
            )}

            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={handleFinalReservation}
                title="수업 예약"
                message={`${pendingReservation?.date} ${pendingReservation?.time}\n예약하시겠습니까?`}
            />

            {alertModal.isOpen && (
                <ConfirmModal
                    isOpen={alertModal.isOpen}
                    onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
                    onConfirm={() => setAlertModal({ ...alertModal, isOpen: false })}
                    title={alertModal.title}
                    message={alertModal.message}
                    confirmText="확인"
                />
            )}
        </div>);
}

const staticStyles = {
    backgroundWrapper: {
        background: '#0D042D', width: '100%', height: '100dvh', display: 'flex', flexDirection: 'column',
        alignItems: 'stretch', margin: '0 auto', boxSizing: 'border-box',
    },
    mainContent: {
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '19px',
        padding: '0 24px 100px 24px',
    },
    branchTitleContainer: { width: '100%', textAlign: 'left', },
    branchTitle: {
        fontFamily: 'Pretendard', fontWeight: '700', fontSize: '18px', color: '#FCFCFA', margin: 0, lineHeight: '1.3',
    },
    boostSelectContainer: {
        width: '100%', height: '41px', borderBottom: '1px solid #1D1A3E', display: 'flex',
        alignItems: 'flex-start', gap: '24px', boxSizing: 'border-box', flexShrink: 0, marginBottom: '19px',
    },
    boostButtonBase: {
        background: 'transparent', border: 'none', padding: 0, margin: 0, cursor: 'pointer',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '41px',
    },
    boostButtonTextBase: {
        fontFamily: 'Pretendard', fontWeight: '700', fontSize: '18px', lineHeight: '17px', verticalAlign: 'middle',
    },
    boostButtonTextActive: { color: '#FFFFFF' },
    boostButtonTextInactive: { color: '#747474' },
    boostButtonIndicatorBase: {
        marginTop: '10px', width: '100%', height: '3px', borderRadius: '1.5px', transition: 'background-color 0.3s',
    },
    boostButtonIndicatorActive: { background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)', },
    boostButtonIndicatorInactive: { background: 'transparent' },
    boostCoachCardList: { width: '100%', display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px', },
    mySubProdContainer: { width: '100%', display: 'flex', flexDirection: 'column', gap: '18px' },
    mySubProdHeader: { fontFamily: 'Pretendard', fontWeight: 600, fontSize: '20px', color: '#FFFFFF' },
    mySubProdBox: {
        width: '100%', background: '#FCFCFA66', border: '1px solid #9A9A9A', borderRadius: '10px', overflow: 'hidden'
    },
    mySubProdTitle: {
        width: '100%', background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)', padding: '20px', boxSizing: 'border-box'
    },
    mySubProdName: {
        display: 'flex', alignItems: 'center', fontFamily: 'Pretendard', fontWeight: 600, fontSize: '16px', color: '#FCFCFA', marginBottom: '20px'
    },
    crownIcon: { width: '20px', height: '18px', marginLeft: '8px' },
    mySubState: {
        marginLeft: 'auto', borderRadius: '20px', border: '1px solid #FCFCFA', padding: '4px 12px', fontSize: '12px', fontWeight: 500, color: '#FFFFFF'
    },
    cardDivider: { height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.2)', width: '100%', marginBottom: '20px' },
    subPeriod: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', fontFamily: 'Pretendard',
    },
    subPeriodValue: { color: '#FFFFFF', fontWeight: 600, },
};

const scheduleStyles = {
    dayContainer: { width: '100%', display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' },
    dayHeader: { width: '100%', padding: '0 4px' },
    dayLabel: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '18px', color: '#FFFFFF' },
    classList: { display: 'flex', flexDirection: 'column', gap: '16px' },
    classCard: {
        width: '100%', background: '#1B1931', borderRadius: '10px', padding: '16px', boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column', gap: '16px', cursor: 'pointer'
    },
    classInfoMain: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
    classImg: { width: '80px', height: '80px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 },
    classTextContent: { flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' },
    className: { fontFamily: 'Pretendard', fontWeight: 600, fontSize: '16px', color: '#FCFCFA', margin: 0 },
    classIntro: {
        fontFamily: 'Pretendard', fontWeight: 400, fontSize: '13px', color: '#9A9A9A', margin: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
    },
    timeSlotList: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    timeSlot: {
        background: '#0D042D', border: '1px solid #8052FF', borderRadius: '6px',
        padding: '6px 10px', display: 'flex', alignItems: 'center', justifyContent: 'center'
    },
    timeSlotDisabled: { background: '#333333', border: '1px solid #555555', opacity: 0.6 },
    timeSlotText: { fontFamily: 'Pretendard', fontWeight: 500, fontSize: '12px', color: '#FFFFFF' }
};

const myReserveStyles = {
    container: { width: '100%', display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '48px', },
    titleWrapper: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', width: '100%', },
    title: { fontFamily: 'Pretendard', fontWeight: 600, fontSize: '20px', color: '#FFFFFF', },
    arrowIcon: { width: '7px', height: '14px', color: '#FCFCFA', marginLeft: 'auto', },
    listContainer: {
        display: 'flex', flexDirection: 'row', gap: '12px', overflowX: 'auto', scrollSnapType: 'x mandatory',
        width: 'calc(100% + 48px)', marginLeft: '-24px', marginRight: '-24px', padding: '0 24px',
        boxSizing: 'border-box', scrollbarWidth: 'none',
    },
    card: {
        minWidth: 'calc(100vw - 48px)', flexShrink: 0, scrollSnapAlign: 'center', height: '92px',
        borderRadius: '10px', background: '#1B1931',
        display: 'flex', alignItems: 'center', padding: '16px', boxSizing: 'border-box', cursor: 'pointer',
    },
    coachImg: { width: '60px', height: '60px', borderRadius: '8px', objectFit: 'cover', marginRight: '16px', },
    infoContainer: { display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '8px', },
    coachName: { fontFamily: 'Pretendard', fontWeight: 600, fontSize: '16px', color: '#FCFCFA', },
    dateTimeInfo: { display: 'flex', alignItems: 'center', gap: '8px', },
    dateWrapper: { display: 'flex', alignItems: 'center', gap: '4px', },
    timeWrapper: { display: 'flex', alignItems: 'center', gap: '4px', },
    icon: { width: '15px', height: '15px', color: '#9A9A9A', },
    dateText: { fontFamily: 'Pretendard', fontWeight: 500, fontSize: '12px', color: '#FFFFFF', },
    timeText: { fontFamily: 'Pretendard', fontWeight: 500, fontSize: '12px', color: '#FFFFFF', },
    dotDivider: { width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#FFFFFF50', },
};

export default Ipt;
